import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

const InvoicePage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [billToName, setBillToName] = useState('');
  const [billToPhone, setBillToPhone] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [dateDue, setDateDue] = useState('');
  const [advisorName, setAdvisorName] = useState('');
  const [advisorContact, setAdvisorContact] = useState('');
  const [advisorEmail, setAdvisorEmail] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [serviceStart, setServiceStart] = useState('');
  const [serviceEnd, setServiceEnd] = useState('');
  const [weeklyDay, setWeeklyDay] = useState('');
  const [serviceStartTime, setServiceStartTime] = useState('');
  const [serviceEndTime, setServiceEndTime] = useState('');
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState('');
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState(false);
  const [newInvoiceNumber, setNewInvoiceNumber] = useState('');
  const [includePaymentAdvisor, setIncludePaymentAdvisor] = useState(true);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [customInvoiceName, setCustomInvoiceName] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  // Load default invoice settings
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/billing/invoice/defaults`)
      .then(({ data }) => {
        setServiceStart(data.service_start || '');
        setServiceEnd(data.service_end || '');
        setDescription(data.description || '');
        setHours(data.hours?.toString() || '');
        setUnitPrice(data.unit_price?.toString() || '');
        setBillToName(data.bill_to_name || '');
        setBillToPhone(data.bill_to_phone || '');
        setBillToEmail(data.bill_to_email || '');
        // Use due_date_input for the date input field if available
        setDateDue(data.due_date_input || '');
        setIsRecurring(!!data.recurring);
        setWeeklyDay(data.weekly_day || '');
        setAdvisorName(data.payment_advisor_name || '');
        setAdvisorContact(data.payment_advisor_contact || '');
        setAdvisorEmail(data.payment_advisor_email || '');
      })
      .catch((err) => console.error('Failed to load invoice defaults', err));
  }, []);

  // Load current invoice number
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/billing/invoice/number`)
      .then(({ data }) => {
        setCurrentInvoiceNumber(data.invoiceNumber);
        setNewInvoiceNumber(data.invoiceNumber);
      })
      .catch((err) => console.error('Failed to load invoice number', err));
  }, []);

  // Load clients
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/clients/unified`)
      .then(({ data }) => {
        // Handle both array response and object response
        setClients(Array.isArray(data) ? data : (data.clients || []));
      })
      .catch((err) => {
        console.error('Failed to load clients', err);
        setClients([]); // Set empty array on error
      });
  }, []);

  // Initialize email template with custom message or default content
  useEffect(() => {
    const selectedClient = clients.find(client => client.id === parseInt(selectedClientId));
    const clientName = selectedClient 
      ? (selectedClient.first_name || selectedClient.last_name 
        ? `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim()
        : selectedClient.bill_to_name || 'Customer')
      : (billToName || 'Customer');
    const nextInvoiceNumber = customInvoiceName ? customInvoiceName : (currentInvoiceNumber ? (() => {
      const prefix = currentInvoiceNumber.substring(0, currentInvoiceNumber.length - 2);
      const num = parseInt(currentInvoiceNumber.slice(-2)) + 1;
      return `${prefix}${num.toString().padStart(2, '0')}`;
    })() : 'HUHC01');
    
    // Use custom message if available, otherwise use default template
    if (customMessage) {
      // Replace {INVOICE_NUMBER} placeholder with actual invoice number
      const processedMessage = customMessage.replace(/{INVOICE_NUMBER}/g, nextInvoiceNumber);
      setEmailTemplate(processedMessage);
    } else {
      setEmailTemplate(`Dear ${clientName},

Thank you for your continued trust in our services. Please find the attached invoice ${nextInvoiceNumber} for your review.

Best regards,
Hearts United Healthcare`);
    }
  }, [clients, selectedClientId, billToName, currentInvoiceNumber, customInvoiceName, customMessage]);

  // Dynamically update description based on service date & time
  useEffect(() => {
    if (serviceStart && serviceEnd && serviceStartTime && serviceEndTime) {
      const shiftType = serviceEndTime <= serviceStartTime ? 'Overnight' : 'Day';
      
      // Format dates nicely
      const startDate = new Date(serviceStart);
      const endDate = new Date(serviceEnd);
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
      
      let dateRange;
      if (startDate.getMonth() === endDate.getMonth()) {
        dateRange = `${monthNames[startDate.getMonth()]} ${startDate.getDate()} – ${endDate.getDate()}, ${endDate.getFullYear()}`;
      } else {
        dateRange = `${monthNames[startDate.getMonth()]} ${startDate.getDate()} – ${monthNames[endDate.getMonth()]} ${endDate.getDate()}, ${endDate.getFullYear()}`;
      }
      
      setDescription(
        `${shiftType} care for 7 days (${dateRange}) - Client: ${billToName || 'Janet Richard'}`
      );
    }
  }, [serviceStart, serviceEnd, serviceStartTime, serviceEndTime, billToName]);

  // Handle client selection
  const handleClientSelect = async (clientId) => {
    setSelectedClientId(clientId);
    if (clientId) {
      const selectedClient = clients.find(client => client.id === parseInt(clientId));
      if (selectedClient) {
        const clientName = selectedClient.first_name || selectedClient.last_name 
          ? `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim()
          : selectedClient.bill_to_name || '';
        
        setBillToName(clientName);
        const clientEmail = selectedClient.email || selectedClient.bill_to_email || '';
        setBillToEmail(clientEmail);
        setEmailTo(clientEmail); // Also set the To field for email sending
        setBillToPhone(selectedClient.phone_number || selectedClient.bill_to_phone || '');
        setUnitPrice(selectedClient.hourly_rate?.toString() || '');
        
        // Fetch client-specific defaults
        try {
          const response = await axios.get(`${API_BASE_URL}/billing/client-defaults/${encodeURIComponent(clientName)}`);
          if (response.data.success && response.data.defaults) {
            setEmailCc(response.data.defaults.default_cc_emails || '');
            setEmailBcc(response.data.defaults.default_bcc_emails || '');
            setCustomMessage(response.data.defaults.default_email_message || '');
          }
        } catch (err) {
          console.error('Failed to fetch client defaults:', err);
          // Don't show error, just use empty defaults
          setEmailCc('');
          setEmailBcc('');
          setCustomMessage('');
        }
      }
    } else {
      // Clear all fields when no client is selected
      setBillToName('');
      setBillToEmail('');
      setBillToPhone('');
      setEmailTo('');
      setEmailCc('');
      setEmailBcc('');
      setCustomMessage('');
      setUnitPrice('');
    }
  };



  const updateInvoiceNumber = async () => {
    try {
      const response = await axios.put(`${API_BASE_URL}/billing/invoice/number`, {
        invoiceNumber: newInvoiceNumber
      });
      if (response.data.success) {
        setCurrentInvoiceNumber(newInvoiceNumber);
        setEditingInvoiceNumber(false);
        setSuccessMessage('Invoice number updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Failed to update invoice number:', err);
      setError(err.response?.data?.message || 'Failed to update invoice number');
      setTimeout(() => setError(''), 3000);
    }
  };

  const generateInvoice = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      // Format the due date for the backend (convert YYYY-MM-DD to "Monday, M/D/YYYY")
      let formattedDueDate = dateDue;
      if (dateDue && dateDue.includes('-')) {
        // Parse date in local timezone to avoid date shifting
        const [year, month, day] = dateDue.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayOfWeek = dayNames[date.getDay()];
        formattedDueDate = `${dayOfWeek}, ${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      }
      
      // Build request data, only including non-empty values
      const requestData = {};
      
      // Only add fields that have values
      if (serviceStart) requestData.service_start = serviceStart;
      if (serviceEnd) requestData.service_end = serviceEnd;
      if (serviceStartTime) requestData.service_start_time = serviceStartTime;
      if (serviceEndTime) requestData.service_end_time = serviceEndTime;
      if (description) requestData.description = description;
      if (hours) requestData.hours = Number(hours);
      if (unitPrice) requestData.unit_price = Number(unitPrice);
      if (billToName) requestData.bill_to_name = billToName;
      if (billToPhone) requestData.bill_to_phone = billToPhone;
      if (billToEmail) requestData.bill_to_email = billToEmail;
      if (formattedDueDate) requestData.due_date = formattedDueDate;
      requestData.recurring = isRecurring;
      if (isRecurring && weeklyDay) requestData.weekly_day = weeklyDay;
      
      // Only include payment advisor info if checkbox is checked
      requestData.include_payment_advisor = includePaymentAdvisor;
      if (includePaymentAdvisor) {
        if (advisorName) requestData.payment_advisor_name = advisorName;
        if (advisorContact) requestData.payment_advisor_contact = advisorContact;
        if (advisorEmail) requestData.payment_advisor_email = advisorEmail;
      }
      
      // Add custom invoice name if provided
      if (customInvoiceName) {
        requestData.custom_invoice_name = customInvoiceName;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/billing/invoice`,
        requestData
      );
      
      if (response.data.success) {
        const { invoiceUrl, invoiceId } = response.data;
        setSuccessMessage('Invoice generated successfully.');
        setPreviewUrl(invoiceUrl);
        
        // Open preview page in new tab
        const previewUrl = `/invoice-preview/${invoiceId}`;
        window.open(previewUrl, '_blank');
        
        // Show email template and PDF preview options
        setShowEmailTemplate(true);
        setShowPreview(true);
        
        // Initialize email To field with bill to email
        setEmailTo(billToEmail || '');
        
        // Optionally, also provide direct download link
        if (invoiceUrl) {
          // Create a temporary link to download from S3
          const link = document.createElement('a');
          link.href = invoiceUrl;
          link.target = '_blank';
          link.setAttribute('download', response.data.filename || 'invoice.pdf');
          // Don't auto-click to avoid popup blockers
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate invoice');
      }
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      setError(err.response?.data?.message || 'Failed to generate invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <h2>Generate Invoice</h2>
      
      {/* Invoice Number Management Section */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Current Invoice Number</h5>
          {!editingInvoiceNumber ? (
            <div className="d-flex align-items-center">
              <p className="mb-0">Next invoice will be: <strong>Invoice number: {customInvoiceName ? 
                customInvoiceName : 
                (currentInvoiceNumber ? 
                  (() => {
                    const prefix = currentInvoiceNumber.substring(0, currentInvoiceNumber.length - 2);
                    const num = parseInt(currentInvoiceNumber.slice(-2)) + 1;
                    return `${prefix}${num.toString().padStart(2, '0')}`;
                  })()
                  : 'HUHC01')}</strong></p>
              <button 
                className="btn btn-sm btn-secondary ms-3"
                onClick={() => setEditingInvoiceNumber(true)}
              >
                Edit Last Invoice Number
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control me-2"
                style={{ width: '150px' }}
                value={newInvoiceNumber}
                onChange={(e) => setNewInvoiceNumber(e.target.value.toUpperCase())}
                placeholder="HUHC35"
              />
              <button 
                className="btn btn-sm btn-primary me-2"
                onClick={updateInvoiceNumber}
              >
                Save
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setEditingInvoiceNumber(false);
                  setNewInvoiceNumber(currentInvoiceNumber);
                }}
              >
                Cancel
              </button>
            </div>
          )}
          <small className="text-muted">Last used: {currentInvoiceNumber}</small>
        </div>
      </div>
      
      {/* Custom Invoice Name Section */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Custom Invoice Name (Optional)</h5>
          <div className="mb-3">
            <label className="form-label">Custom Invoice Filename</label>
            <input
              type="text"
              className="form-control"
              value={customInvoiceName}
              onChange={(e) => setCustomInvoiceName(e.target.value)}
              placeholder="e.g., ClientName-July2025-Invoice"
            />
            <small className="text-muted">
              If provided, this will override the default invoice number filename. Leave blank to use the default invoice number.
            </small>
          </div>
        </div>
      </div>
      
      <div className="row mb-3">
        <div className="col">
          <label className="form-label">Service From (Date)</label>
          <input
            type="date"
            className="form-control"
            value={serviceStart}
            onChange={(e) => setServiceStart(e.target.value)}
          />
        </div>
        <div className="col">
          <label className="form-label">Service To (Date)</label>
          <input
            type="date"
            className="form-control"
            value={serviceEnd}
            onChange={(e) => setServiceEnd(e.target.value)}
          />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col">
          <label className="form-label">Service From (Time)</label>
          <input
            type="time"
            className="form-control"
            value={serviceStartTime}
            onChange={(e) => setServiceStartTime(e.target.value)}
          />
        </div>
        <div className="col">
          <label className="form-label">Service To (Time)</label>
          <input
            type="time"
            className="form-control"
            value={serviceEndTime}
            onChange={(e) => setServiceEndTime(e.target.value)}
          />
        </div>
      </div>
      <form className="mb-4">
        <div className="mb-3">
          <label className="form-label">Select Client</label>
          <select
            className="form-select"
            value={selectedClientId}
            onChange={(e) => handleClientSelect(e.target.value)}
          >
            <option value="">-- Select a client --</option>
            {Array.isArray(clients) && clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.first_name || client.last_name 
                  ? `${client.first_name || ''} ${client.last_name || ''}`.trim()
                  : client.bill_to_name || 'Unknown Client'} {client.email && `(${client.email})`}
              </option>
            ))}
          </select>
          <small className="text-muted">
            Selecting a client will automatically populate their information below.
          </small>
        </div>
        <div className="mb-3">
          <label className="form-label">Service Description</label>
          <textarea
            className="form-control"
            rows="2"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="row mb-3">
          <div className="col">
            <label className="form-label">Hours</label>
            <input
              type="number"
              className="form-control"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </div>
          <div className="col">
            <label className="form-label">Unit Price</label>
            <input
              type="number"
              className="form-control"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Bill To Name</label>
          <input
            type="text"
            className="form-control"
            value={billToName}
            onChange={(e) => setBillToName(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Bill To Phone</label>
          <input
            type="text"
            className="form-control"
            value={billToPhone}
            onChange={(e) => setBillToPhone(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Bill To Email</label>
          <input
            type="email"
            className="form-control"
            value={billToEmail}
            onChange={(e) => setBillToEmail(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Custom Email Message (Client Default)</label>
          <textarea
            className="form-control"
            rows="4"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="This will be auto-loaded based on selected client. Use {INVOICE_NUMBER} to insert invoice number."
          />
          <small className="text-muted">This message will be used when sending the invoice via email.</small>
        </div>
      <div className="mb-3 form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="recurringCheck"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="recurringCheck">
            Recurring Invoice?
          </label>
      </div>
      {isRecurring && (
        <div className="mb-3">
          <label className="form-label">Recurring Day</label>
          <select
            className="form-select"
            value={weeklyDay}
            onChange={(e) => setWeeklyDay(e.target.value)}
          >
            <option value="">-- Select Day --</option>
            {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      )}
      <div className="mb-3">
        <label className="form-label">Date Due</label>
        <input
          type="date"
          className="form-control"
          value={dateDue}
          onChange={(e) => setDateDue(e.target.value)}
        />
      </div>
        
        <div className="mb-3 form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="includePaymentAdvisor"
            checked={includePaymentAdvisor}
            onChange={(e) => setIncludePaymentAdvisor(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="includePaymentAdvisor">
            Include Payment Advisor Information
          </label>
        </div>
        
        {includePaymentAdvisor && (
          <>
            <h5>Payment Advisor</h5>
            <div className="mb-3">
          <label className="form-label">Advisor Name</label>
          <input
            type="text"
            className="form-control"
            value={advisorName}
            onChange={(e) => setAdvisorName(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Advisor Contact</label>
          <input
            type="text"
            className="form-control"
            value={advisorContact}
            onChange={(e) => setAdvisorContact(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Advisor Email</label>
          <input
            type="email"
            className="form-control"
            value={advisorEmail}
            onChange={(e) => setAdvisorEmail(e.target.value)}
          />
        </div>
          </>
        )}
      </form>
      <button
        className="btn btn-primary"
        onClick={generateInvoice}
        disabled={loading}
      >
        {loading ? 'Generating...' : 'Generate Invoice'}
      </button>
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      {successMessage && <div className="alert alert-success mt-3">{successMessage}</div>}
      
      {/* Email Template Section */}
      {showEmailTemplate && (
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">Send Invoice Via Email</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label">To</label>
              <input
                type="email"
                className="form-control"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="recipient@example.com"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">CC (comma separated)</label>
              <input
                type="text"
                className="form-control"
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                placeholder="cc1@example.com, cc2@example.com"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">BCC (comma separated)</label>
              <input
                type="text"
                className="form-control"
                value={emailBcc}
                onChange={(e) => setEmailBcc(e.target.value)}
                placeholder="bcc1@example.com, bcc2@example.com"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Email Template</label>
              <textarea
                className="form-control"
                rows="6"
                value={emailTemplate}
                onChange={(e) => setEmailTemplate(e.target.value)}
                placeholder="Enter your email message here..."
              />
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-success">
                <i className="bi bi-envelope me-2"></i>
                Send Email
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => setShowEmailTemplate(false)}
              >
                Hide Email Template
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* PDF Preview Section */}
      {showPreview && previewUrl && (
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">PDF Preview</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <iframe
                src={previewUrl}
                width="100%"
                height="600px"
                title="Invoice PDF Preview"
                className="border rounded"
              />
            </div>
            <div className="d-flex gap-2">
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <i className="bi bi-download me-2"></i>
                Download PDF
              </a>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => setShowPreview(false)}
              >
                Hide Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePage;

